import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, HelpCircle, Scissors, Ruler, Clock, CreditCard, Truck, Shield } from "lucide-react";

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqCategories = [
    {
      title: "Getting Started",
      icon: HelpCircle,
      questions: [
        {
          q: "How do I book an appointment?",
          a: "You can book an appointment through our website by clicking the 'Book Appointment' button. Select your preferred date, time, and service type. You'll receive a confirmation email with all the details. Appointments typically last 1-2 hours for initial consultations."
        },
        {
          q: "What should I bring to my first appointment?",
          a: "For your first appointment, we recommend bringing any reference images of styles you like, a clear idea of the occasion(s) you're dressing for, and comfortable undergarments similar to what you'll wear with your finished garment. If you have existing garments that fit well, bring those too for reference."
        },
        {
          q: "Do I need an appointment for alterations?",
          a: "Yes, we recommend booking an appointment for alterations to ensure we have adequate time to assess your garment and discuss the needed modifications. Walk-ins may be accommodated based on availability."
        }
      ]
    },
    {
      title: "Custom Garments",
      icon: Scissors,
      questions: [
        {
          q: "What's the difference between bespoke and made-to-measure?",
          a: "Bespoke garments are created entirely from scratch with a unique pattern made specifically for you. Made-to-measure starts with a base pattern that's adjusted to your measurements. Both offer excellent fit, but bespoke provides the highest level of customization for unique body types and styling preferences."
        },
        {
          q: "What fabrics do you offer?",
          a: "We offer a wide selection of premium fabrics from renowned mills including Italian woolens, English tweeds, luxury linens, and performance blends. Our consultants will guide you through options based on your climate, occasion, and personal preferences."
        },
        {
          q: "Can I customize every detail of my suit?",
          a: "Absolutely! From lapel style, button stance, and pocket placement to lining, buttonholes, and monogramming—every detail can be personalized. Our consultants will walk you through all available options."
        }
      ]
    },
    {
      title: "Measurements & Fitting",
      icon: Ruler,
      questions: [
        {
          q: "How many fittings will I need?",
          a: "Typically, custom suits require 2-3 fittings: an initial consultation with measurements, a basted fitting to check structure and fit, and a final fitting for finishing touches. Some garments may need additional fittings for perfection."
        },
        {
          q: "Can you work with my existing measurements?",
          a: "While we can reference previous measurements as a starting point, we always recommend taking fresh measurements in our studio to ensure accuracy. Bodies change, and precision is essential for the perfect fit."
        },
        {
          q: "What if I gain or lose weight during the process?",
          a: "Minor weight fluctuations can usually be accommodated during fittings. For significant changes, we may need to adjust the timeline or construction approach. Please communicate any concerns with your consultant."
        }
      ]
    },
    {
      title: "Timeline & Delivery",
      icon: Clock,
      questions: [
        {
          q: "How long does a custom suit take?",
          a: "Standard turnaround is 6-8 weeks for made-to-measure suits and 10-12 weeks for full bespoke. Rush orders may be possible for an additional fee, subject to our production schedule."
        },
        {
          q: "Can I get my suit expedited?",
          a: "Rush services are available for select orders. Please discuss your timeline needs during your consultation, and we'll do our best to accommodate your schedule."
        },
        {
          q: "Do you offer shipping?",
          a: "We prefer in-person final fittings to ensure perfection, but we can arrange insured shipping for clients who cannot visit our studio. Final adjustments would need to be handled locally."
        }
      ]
    },
    {
      title: "Pricing & Payment",
      icon: CreditCard,
      questions: [
        {
          q: "How much does a custom suit cost?",
          a: "Custom suit pricing varies based on fabric selection, level of customization, and construction method. Made-to-measure suits start at around $1,200, while full bespoke begins at $2,500. Your consultant will provide detailed pricing during your consultation."
        },
        {
          q: "What payment methods do you accept?",
          a: "We accept all major credit cards, bank transfers, and can arrange payment plans for larger orders. A 50% deposit is required to begin production, with the balance due at final delivery."
        },
        {
          q: "Are alterations included in the price?",
          a: "Yes! We include complimentary alterations within 60 days of garment completion. This covers any adjustments needed as you wear your new garment and it settles to your body."
        }
      ]
    },
    {
      title: "Care & Warranty",
      icon: Shield,
      questions: [
        {
          q: "How should I care for my custom garment?",
          a: "We recommend professional dry cleaning for suits and tailored garments. Between cleanings, use a quality brush to remove dust and hang on proper suit hangers. Steam can help release wrinkles. We'll provide detailed care instructions with your garment."
        },
        {
          q: "Do you offer any warranty?",
          a: "We stand behind our craftsmanship with a lifetime warranty on construction. This covers any defects in workmanship, buttons coming loose, seam failures, or similar issues. Normal wear and fabric aging are not covered."
        },
        {
          q: "Can you repair or restore older suits?",
          a: "Yes, we offer repair and restoration services for both our garments and quality pieces from other makers. Contact us for an assessment and quote."
        }
      ]
    }
  ];

  const toggleQuestion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div data-testid="faq-page" className="min-h-screen pt-20">
      {/* Header */}
      <section className="py-16 md:py-20 border-b border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="mono-text text-primary mb-6">Questions Answered</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-6">
              Frequently Asked Questions
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto font-light text-lg">
              Everything you need to know about our custom tailoring process.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1000px] mx-auto px-6 md:px-12">
          {faqCategories.map((category, catIndex) => (
            <motion.div
              key={category.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: catIndex * 0.1 }}
              className="mb-12"
            >
              {/* Category Header */}
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <category.icon size={20} className="text-primary" />
                </div>
                <h2 className="font-serif text-2xl">{category.title}</h2>
              </div>

              {/* Questions */}
              <div className="space-y-3">
                {category.questions.map((faq, faqIndex) => {
                  const globalIndex = `${catIndex}-${faqIndex}`;
                  const isOpen = openIndex === globalIndex;
                  
                  return (
                    <div
                      key={faqIndex}
                      className="border border-white/10 bg-card/50"
                    >
                      <button
                        onClick={() => toggleQuestion(globalIndex)}
                        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                      >
                        <span className="font-medium pr-4">{faq.q}</span>
                        <motion.div
                          animate={{ rotate: isOpen ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <ChevronDown size={20} className="text-primary flex-shrink-0" />
                        </motion.div>
                      </button>
                      
                      <AnimatePresence>
                        {isOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden"
                          >
                            <p className="px-6 pb-4 text-muted-foreground leading-relaxed border-t border-white/5 pt-4">
                              {faq.a}
                            </p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 border-t border-white/5">
        <div className="max-w-[1000px] mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h3 className="font-serif text-2xl mb-4">Still have questions?</h3>
            <p className="text-muted-foreground mb-6">
              We're here to help. Reach out to us directly.
            </p>
            <a
              href="mailto:hello@laguerre.co"
              className="inline-flex items-center gap-2 text-primary hover:underline"
            >
              hello@laguerre.co
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
